// IANA tzdb 2026c zonenow.tab values. The source archive is not bundled or
// fetched at runtime; this is the checked-in selection list for current clocks.

export type ScheduleTimezoneOption = {
    value: string;
    cities: string[];
    searchText: string;
};

export const DEFAULT_SCHEDULE_TIMEZONE = "Asia/Hong_Kong";

export const IANA_TIMEZONE_VALUES = [
    "Pacific/Pago_Pago",
    "Pacific/Niue",
    "Pacific/Honolulu",
    "Pacific/Tahiti",
    "America/Adak",
    "Pacific/Marquesas",
    "Pacific/Gambier",
    "America/Anchorage",
    "Pacific/Pitcairn",
    "America/Los_Angeles",
    "America/Vancouver",
    "America/Phoenix",
    "America/Denver",
    "America/Edmonton",
    "Pacific/Galapagos",
    "America/Mexico_City",
    "Pacific/Easter",
    "America/Chicago",
    "America/Lima",
    "America/Jamaica",
    "America/Havana",
    "America/New_York",
    "America/Caracas",
    "America/Santo_Domingo",
    "America/Santiago",
    "America/Halifax",
    "America/St_Johns",
    "America/Sao_Paulo",
    "America/Miquelon",
    "America/Noronha",
    "America/Nuuk",
    "Atlantic/Cape_Verde",
    "Atlantic/Azores",
    "Africa/Abidjan",
    "Europe/London",
    "Europe/Lisbon",
    "Antarctica/Troll",
    "Africa/Algiers",
    "Africa/Lagos",
    "Europe/Dublin",
    "Africa/Casablanca",
    "Europe/Paris",
    "Africa/Maputo",
    "Africa/Tripoli",
    "Africa/Johannesburg",
    "Europe/Athens",
    "Africa/Cairo",
    "Asia/Beirut",
    "Asia/Gaza",
    "Asia/Jerusalem",
    "Europe/Istanbul",
    "Africa/Nairobi",
    "Europe/Moscow",
    "Asia/Tehran",
    "Asia/Dubai",
    "Asia/Kabul",
    "Asia/Tashkent",
    "Asia/Karachi",
    "Asia/Colombo",
    "Asia/Kolkata",
    "Asia/Kathmandu",
    "Asia/Dhaka",
    "Asia/Yangon",
    "Asia/Bangkok",
    "Asia/Jakarta",
    "Asia/Singapore",
    "Australia/Perth",
    "Asia/Shanghai",
    "Asia/Hong_Kong",
    "Asia/Manila",
    "Asia/Makassar",
    "Australia/Eucla",
    "Asia/Chita",
    "Asia/Tokyo",
    "Asia/Seoul",
    "Asia/Jayapura",
    "Australia/Darwin",
    "Australia/Adelaide",
    "Asia/Vladivostok",
    "Australia/Brisbane",
    "Pacific/Guam",
    "Australia/Sydney",
    "Australia/Lord_Howe",
    "Pacific/Bougainville",
    "Pacific/Norfolk",
    "Asia/Kamchatka",
    "Pacific/Auckland",
    "Pacific/Chatham",
    "Pacific/Tongatapu",
    "Pacific/Kiritimati",
] as const;

const NEARBY_TIMEZONE_CITIES: Record<string, string[]> = {
    "Pacific/Pago_Pago": ["Pago Pago","Apia","Midway"],
    "Pacific/Niue": ["Alofi"],
    "Pacific/Honolulu": ["Honolulu","Hilo","Hawaii"],
    "Pacific/Tahiti": ["Papeete","Rarotonga"],
    "America/Adak": ["Adak","Dutch Harbor"],
    "Pacific/Marquesas": ["Taiohae","Marquesas"],
    "Pacific/Gambier": ["Rikitea","Gambier"],
    "America/Anchorage": ["Anchorage","Fairbanks","Juneau"],
    "Pacific/Pitcairn": ["Adamstown","Pitcairn"],
    "America/Los_Angeles": ["Los Angeles","San Francisco","Seattle","Las Vegas","Tijuana"],
    "America/Vancouver": ["Vancouver","Victoria","Kelowna"],
    "America/Phoenix": ["Phoenix","Tucson","Flagstaff"],
    "America/Denver": ["Denver","Salt Lake City","Albuquerque","Boise"],
    "America/Edmonton": ["Edmonton","Calgary","Red Deer"],
    "Pacific/Galapagos": ["Galapagos","Quito","Guayaquil"],
    "America/Mexico_City": ["Mexico City","Guadalajara","Monterrey","Guatemala City"],
    "Pacific/Easter": ["Hanga Roa","Easter Island"],
    "America/Chicago": ["Chicago","Dallas","Houston","New Orleans","Winnipeg"],
    "America/Lima": ["Lima","Bogota","Medellin"],
    "America/Jamaica": ["Kingston","Panama City"],
    "America/Havana": ["Havana"],
    "America/New_York": ["New York","Washington","Boston","Miami","Philadelphia","Toronto","Montreal"],
    "America/Caracas": ["Caracas","La Paz"],
    "America/Santo_Domingo": ["Santo Domingo","San Juan","Port-au-Prince"],
    "America/Santiago": ["Santiago","Valparaiso"],
    "America/Halifax": ["Halifax","Bermuda","Charlottetown"],
    "America/St_Johns": ["St John's","Newfoundland"],
    "America/Sao_Paulo": ["Sao Paulo","Rio de Janeiro","Brasilia","Belo Horizonte"],
    "America/Miquelon": ["Saint-Pierre","Miquelon"],
    "America/Noronha": ["Fernando de Noronha"],
    "America/Nuuk": ["Nuuk","Greenland"],
    "Atlantic/Cape_Verde": ["Praia","Mindelo"],
    "Atlantic/Azores": ["Ponta Delgada","Azores"],
    "Africa/Abidjan": ["Abidjan","Accra","Reykjavik"],
    "Europe/London": ["London","Manchester","Edinburgh","Belfast"],
    "Europe/Lisbon": ["Lisbon","Porto","Madrid"],
    "Antarctica/Troll": ["Troll Station"],
    "Africa/Algiers": ["Algiers","Tunis"],
    "Africa/Lagos": ["Lagos","Abuja","Kano"],
    "Europe/Dublin": ["Dublin","Cork","Limerick"],
    "Africa/Casablanca": ["Casablanca","Rabat","Marrakesh"],
    "Europe/Paris": ["Paris","Brussels","Amsterdam","Berlin","Madrid","Rome","Zurich"],
    "Africa/Maputo": ["Maputo","Harare","Lusaka"],
    "Africa/Tripoli": ["Tripoli","Benghazi","Kaliningrad"],
    "Africa/Johannesburg": ["Johannesburg","Pretoria","Cape Town","Durban"],
    "Europe/Athens": ["Athens","Bucharest","Sofia","Helsinki","Kyiv"],
    "Africa/Cairo": ["Cairo","Alexandria"],
    "Asia/Beirut": ["Beirut","Damascus","Amman"],
    "Asia/Gaza": ["Gaza","Ramallah"],
    "Asia/Jerusalem": ["Jerusalem","Tel Aviv","Haifa"],
    "Europe/Istanbul": ["Istanbul","Ankara","Minsk"],
    "Africa/Nairobi": ["Nairobi","Mombasa","Addis Ababa","Kampala"],
    "Europe/Moscow": ["Moscow","Saint Petersburg","Nizhny Novgorod"],
    "Asia/Tehran": ["Tehran","Mashhad","Isfahan"],
    "Asia/Dubai": ["Dubai","Abu Dhabi","Muscat","Baku","Tbilisi","Yerevan"],
    "Asia/Kabul": ["Kabul","Kandahar"],
    "Asia/Tashkent": ["Tashkent","Almaty","Bishkek","Dushanbe","Ashgabat","Male"],
    "Asia/Karachi": ["Karachi","Lahore","Islamabad","Peshawar"],
    "Asia/Colombo": ["Colombo","Kandy"],
    "Asia/Kolkata": ["Kolkata","Mumbai","Delhi","Bengaluru","Chennai","Hyderabad"],
    "Asia/Kathmandu": ["Kathmandu","Pokhara"],
    "Asia/Dhaka": ["Dhaka","Chittagong","Thimphu"],
    "Asia/Yangon": ["Yangon","Mandalay","Naypyidaw"],
    "Asia/Bangkok": ["Bangkok","Phnom Penh","Hanoi","Vientiane"],
    "Asia/Jakarta": ["Jakarta","Bandung","Surabaya"],
    "Asia/Singapore": ["Singapore","Kuala Lumpur","Johor Bahru","Bandar Seri Begawan"],
    "Australia/Perth": ["Perth","Fremantle"],
    "Asia/Shanghai": ["Shanghai","Beijing","Chongqing","Harbin","Guangzhou","Shenzhen"],
    "Asia/Hong_Kong": ["Hong Kong","Macau"],
    "Asia/Manila": ["Manila","Quezon City","Cebu City"],
    "Asia/Makassar": ["Makassar","Denpasar","Bali"],
    "Australia/Eucla": ["Eucla"],
    "Asia/Chita": ["Chita","Dili"],
    "Asia/Tokyo": ["Tokyo","Osaka","Kyoto","Yokohama"],
    "Asia/Seoul": ["Seoul","Busan","Incheon"],
    "Asia/Jayapura": ["Jayapura","Ambon"],
    "Australia/Darwin": ["Darwin","Alice Springs"],
    "Australia/Adelaide": ["Adelaide"],
    "Asia/Vladivostok": ["Vladivostok","Khabarovsk","Port Moresby"],
    "Australia/Brisbane": ["Brisbane","Cairns","Townsville"],
    "Pacific/Guam": ["Guam","Hagåtña","Saipan"],
    "Australia/Sydney": ["Sydney","Canberra","Melbourne","Hobart"],
    "Australia/Lord_Howe": ["Lord Howe Island"],
    "Pacific/Bougainville": ["Buka","Honiara","Port Vila"],
    "Pacific/Norfolk": ["Kingston","Norfolk Island"],
    "Asia/Kamchatka": ["Petropavlovsk-Kamchatsky","Fiji","Suva"],
    "Pacific/Auckland": ["Auckland","Wellington","Christchurch"],
    "Pacific/Chatham": ["Chatham Islands","Waitangi"],
    "Pacific/Tongatapu": ["Nuku'alofa","Apia","Tokelau"],
    "Pacific/Kiritimati": ["Kiritimati","Christmas Island"],
};

const TIMEZONE_REGION_HINTS: Record<string, string> = {
    "Pacific/Pago_Pago": "Midway; Samoa (SST)",
    "Pacific/Niue": "Niue",
    "Pacific/Honolulu": "Hawaii (HST)",
    "Pacific/Tahiti": "Tahiti; Cook Islands",
    "America/Adak": "western Aleutians in Alaska (HST/HDT)",
    "Pacific/Marquesas": "Marquesas",
    "Pacific/Gambier": "Gambier",
    "America/Anchorage": "most of Alaska (AKST/AKDT)",
    "Pacific/Pitcairn": "Pitcairn",
    "America/Los_Angeles": "Pacific (PST/PDT) - US; Mexico near US border",
    "America/Vancouver": "Mountain Standard (MST) - British Columbia (most areas)",
    "America/Phoenix": "Mountain Standard (MST) - Arizona; western Mexico; Yukon",
    "America/Denver": "Mountain (MST/MDT) - US; Mexico near US border; northern Canada",
    "America/Edmonton": "Central Standard (CST) - Alberta and some neighbors",
    "Pacific/Galapagos": "Galápagos",
    "America/Mexico_City": "Central Standard (CST) - Saskatchewan; central Mexico; Central America",
    "Pacific/Easter": "Easter Island",
    "America/Chicago": "Central (CST/CDT) - US & Canada; Mexico near US border",
    "America/Lima": "eastern South America",
    "America/Jamaica": "Eastern Standard (EST) - Caymans; Jamaica; eastern Mexico; Panama",
    "America/Havana": "Cuba",
    "America/New_York": "Eastern (EST/EDT) - US & Canada",
    "America/Caracas": "western South America",
    "America/Santo_Domingo": "Atlantic Standard (AST) - eastern Caribbean",
    "America/Santiago": "most of Chile",
    "America/Halifax": "Atlantic (AST/ADT) - Canada; Bermuda",
    "America/St_Johns": "Newfoundland (NST/NDT)",
    "America/Sao_Paulo": "eastern and southern South America",
    "America/Miquelon": "St Pierre & Miquelon",
    "America/Noronha": "Fernando de Noronha; South Georgia",
    "America/Nuuk": "most of Greenland",
    "Atlantic/Cape_Verde": "Cape Verde",
    "Atlantic/Azores": "Azores",
    "Africa/Abidjan": "far western Africa; Iceland (GMT)",
    "Europe/London": "United Kingdom (GMT/BST)",
    "Europe/Lisbon": "western Europe (WET/WEST)",
    "Antarctica/Troll": "Troll Station in Antarctica",
    "Africa/Algiers": "Algeria, Tunisia (CET)",
    "Africa/Lagos": "western Africa (WAT)",
    "Europe/Dublin": "Ireland (IST/GMT)",
    "Africa/Casablanca": "Morocco",
    "Europe/Paris": "central Europe (CET/CEST)",
    "Africa/Maputo": "central Africa (CAT)",
    "Africa/Tripoli": "Libya; Kaliningrad (EET)",
    "Africa/Johannesburg": "southern Africa (SAST)",
    "Europe/Athens": "eastern Europe (EET/EEST)",
    "Africa/Cairo": "Egypt",
    "Asia/Beirut": "Lebanon",
    "Asia/Gaza": "Palestine",
    "Asia/Jerusalem": "Israel",
    "Europe/Istanbul": "Near East; Belarus",
    "Africa/Nairobi": "eastern Africa (EAT)",
    "Europe/Moscow": "Moscow (MSK)",
    "Asia/Tehran": "Iran",
    "Asia/Dubai": "Russia; Caucasus; Persian Gulf; Seychelles; Réunion",
    "Asia/Kabul": "Afghanistan",
    "Asia/Tashkent": "Russia; Kazakhstan; Tajikistan; Turkmenistan; Uzbekistan; Maldives",
    "Asia/Karachi": "Pakistan (PKT)",
    "Asia/Colombo": "Sri Lanka",
    "Asia/Kolkata": "India (IST)",
    "Asia/Kathmandu": "Nepal",
    "Asia/Dhaka": "Russia; Kyrgyzstan; Bhutan; Bangladesh; Chagos",
    "Asia/Yangon": "Myanmar; Cocos",
    "Asia/Bangkok": "Russia; Indochina; Christmas Island",
    "Asia/Jakarta": "Indonesia (WIB)",
    "Asia/Singapore": "Russia; Brunei; Malaysia; Singapore; Concordia",
    "Australia/Perth": "Western Australia (AWST)",
    "Asia/Shanghai": "China (CST)",
    "Asia/Hong_Kong": "Hong Kong (HKT)",
    "Asia/Manila": "Philippines (PHT)",
    "Asia/Makassar": "Indonesia (WITA)",
    "Australia/Eucla": "Eucla",
    "Asia/Chita": "Russia; Palau; East Timor",
    "Asia/Tokyo": "Japan (JST); Eyre Bird Observatory",
    "Asia/Seoul": "Korea (KST)",
    "Asia/Jayapura": "Indonesia (WIT)",
    "Australia/Darwin": "Northern Territory (ACST)",
    "Australia/Adelaide": "South Australia (ACST/ACDT)",
    "Asia/Vladivostok": "Russia; Yap; Chuuk; Papua New Guinea; Dumont d’Urville",
    "Australia/Brisbane": "Queensland (AEST)",
    "Pacific/Guam": "Mariana Islands (ChST)",
    "Australia/Sydney": "southeast Australia (AEST/AEDT)",
    "Australia/Lord_Howe": "Lord Howe Island",
    "Pacific/Bougainville": "Russia; Kosrae; Bougainville; Solomons",
    "Pacific/Norfolk": "Norfolk Island",
    "Asia/Kamchatka": "Russia; Tuvalu; Fiji; etc.",
    "Pacific/Auckland": "New Zealand (NZST/NZDT)",
    "Pacific/Chatham": "Chatham Islands",
    "Pacific/Tongatapu": "Kanton; Tokelau; Samoa (western); Tonga",
    "Pacific/Kiritimati": "Kiritimati",
};

const cityFromTimezone = (value: string) => value
    .split("/")
    .pop()!
    .replace(/_/g, " ");

const normalizeTimezoneSearch = (value: string) => value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase();

export const scheduleTimezoneOptions: ScheduleTimezoneOption[] = IANA_TIMEZONE_VALUES.map((value) => {
    const cities = [cityFromTimezone(value), ...(NEARBY_TIMEZONE_CITIES[value] ?? [])]
        .filter((city, index, all) => all.indexOf(city) === index);
    return {
        value,
        cities,
        searchText: normalizeTimezoneSearch(value + " " + cities.join(" ") + " " + (TIMEZONE_REGION_HINTS[value] ?? "")),
    };
});

const timezoneOffsetCache = new Map<string, string>();

type ScheduleWallTime = {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
    millisecond: number;
};

const timezoneFormatterCache = new Map<string, Intl.DateTimeFormat>();

const formatterForTimezone = (timezone: string) => {
    let formatter = timezoneFormatterCache.get(timezone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat("en-US", {
            timeZone: timezone,
            hourCycle: "h23",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
        timezoneFormatterCache.set(timezone, formatter);
    }
    return formatter;
};

const wallTimeFor = (instant: Date, timezone: string): ScheduleWallTime => {
    const parts = Object.fromEntries(
        formatterForTimezone(timezone).formatToParts(instant)
            .filter((part) => part.type !== "literal")
            .map((part) => [part.type, Number(part.value)])
    ) as Record<string, number>;
    return {
        year: parts.year,
        month: parts.month,
        day: parts.day,
        hour: parts.hour,
        minute: parts.minute,
        second: parts.second,
        millisecond: instant.getUTCMilliseconds(),
    };
};

const wallStamp = (wall: ScheduleWallTime) => Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
    wall.millisecond,
);

const sameWallTime = (left: ScheduleWallTime, right: ScheduleWallTime) =>
    left.year === right.year
    && left.month === right.month
    && left.day === right.day
    && left.hour === right.hour
    && left.minute === right.minute
    && left.second === right.second
    && left.millisecond === right.millisecond;

const timezoneOffsetAt = (instant: Date, timezone: string) =>
    wallStamp(wallTimeFor(instant, timezone)) - instant.getTime();

/** Convert a schedule-local datetime-local value into a UTC ISO instant. */
export const scheduleTimezoneLocalToIso = (value: string, timezone: string): string | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/.exec(value);
    if (!match) return null;
    const wall: ScheduleWallTime = {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
        hour: Number(match[4]),
        minute: Number(match[5]),
        second: Number(match[6] ?? 0),
        millisecond: Number((match[7] ?? "").padEnd(3, "0") || 0),
    };
    const naive = wallStamp(wall);
    if (!Number.isFinite(naive)) return null;

    // Check nearby offsets so both ordinary DST changes and larger historical
    // date-line shifts can be resolved without assuming a fixed offset.
    try {
        const offsets = new Set(
            [-2, -1, 0, 1, 2].map((days) => timezoneOffsetAt(new Date(naive + days * 86_400_000), timezone))
        );
        const matches = [...offsets]
            .map((offset) => new Date(naive - offset))
            .filter((candidate) => sameWallTime(wallTimeFor(candidate, timezone), wall))
            .sort((left, right) => left.getTime() - right.getTime());
        // Ambiguous fall-back times use the earlier instant, matching storage.
        // A nonexistent spring-forward time has no match and is rejected.
        return matches[0]?.toISOString() ?? null;
    } catch {
        return null;
    }
};

/** Format a UTC/API instant as a datetime-local value in an IANA timezone. */
export const scheduleTimezoneToLocalInput = (value: string | null | undefined, timezone: string): string => {
    if (!value) return "";
    const instant = new Date(value);
    if (!Number.isFinite(instant.getTime())) return "";
    try {
        const wall = wallTimeFor(instant, timezone);
        const pad = (number: number) => String(number).padStart(2, "0");
        return `${String(wall.year).padStart(4, "0")}-${pad(wall.month)}-${pad(wall.day)}T${pad(wall.hour)}:${pad(wall.minute)}`;
    } catch {
        return "";
    }
};

export const scheduleTimezoneOffset = (value: string) => {
    const cacheKey = value + ":" + new Date().toISOString().slice(0, 10);
    const cached = timezoneOffsetCache.get(cacheKey);
    if (cached) return cached;
    try {
        const timeZoneName = new Intl.DateTimeFormat("en-US", {
            timeZone: value,
            timeZoneName: "longOffset",
        }).formatToParts(new Date()).find((part) => part.type === "timeZoneName")?.value ?? "GMT";
        const offset = timeZoneName === "GMT" ? "+00:00" : timeZoneName.replace(/^GMT/, "");
        timezoneOffsetCache.set(cacheKey, offset);
        return offset;
    } catch {
        return "+00:00";
    }
};
